<template>
  <div class="inv-tabs-bar">
    <div class="inv-tabs">

      <!-- Product -->
      <router-link
        v-if="permission.inventory_mgt_module == 1 && permission.view_product == 1"
        :to="$url('/all-products')"
        class="inv-tab"
        :class="isActive(['All Products','Create Product','Edit Product','View Product','Bulk Product Upload']) ? 'inv-tab--active' : ''"
      >
        <font-awesome-icon icon="boxes" class="tab-icon" />
        Products
      </router-link>

      <!-- Product Location -->
      <template v-if="permission.inventory_mgt_module == 1 && permission.view_product_location == 1">
        <router-link
          v-if="!freemium"
          :to="$url('/all-product-location')"
          class="inv-tab"
          active-class="inv-tab--active"
        >
          <font-awesome-icon icon="map-marker-alt" class="tab-icon" />
          Product Location
        </router-link>
        <span v-else class="inv-tab inv-tab--locked" @click="freemiumPrompt" title="Upgrade to unlock">
          <font-awesome-icon icon="map-marker-alt" class="tab-icon" />
          Product Location
          <font-awesome-icon icon="lock" class="lock-icon" />
        </span>
      </template>

      <!-- Stock Adjustment -->
      <template v-if="permission.inventory_mgt_module == 1 && permission.view_stock_adjustment == 1">
        <router-link
          v-if="!freemium"
          :to="$url('/all-stock-adjustment')"
          class="inv-tab"
          :class="isActive(['Stock Adjustment','Create Stock Adjustment','View Stock Adjustment','Print Stock Adjustment','Print 80mm Stock Adjustment','Print 58mm Stock Adjustment']) ? 'inv-tab--active' : ''"
        >
          <font-awesome-icon icon="sliders-h" class="tab-icon" />
          Stock Adjustment
        </router-link>
        <span v-else class="inv-tab inv-tab--locked" @click="freemiumPrompt" title="Upgrade to unlock">
          <font-awesome-icon icon="sliders-h" class="tab-icon" />
          Stock Adjustment
          <font-awesome-icon icon="lock" class="lock-icon" />
        </span>
      </template>

      <!-- Stock Transfer -->
      <template v-if="permission.inventory_mgt_module == 1 && permission.view_stock_transfer == 1">
        <router-link
          v-if="!freemium"
          :to="$url('/all-stock-transfer')"
          class="inv-tab"
          :class="isActive(['Stock Transfer','Create Stock Transfer','View Stock Transfer','Print Stock Transfer','Print 80mm Stock Transfer','Print 58mm Stock Transfer']) ? 'inv-tab--active' : ''"
        >
          <font-awesome-icon icon="exchange-alt" class="tab-icon" />
          Stock Transfer
        </router-link>
        <span v-else class="inv-tab inv-tab--locked" @click="freemiumPrompt" title="Upgrade to unlock">
          <font-awesome-icon icon="exchange-alt" class="tab-icon" />
          Stock Transfer
          <font-awesome-icon icon="lock" class="lock-icon" />
        </span>
      </template>

      <!-- Internal Requisitions -->
      <template v-if="permission.inventory_mgt_module == 1 && permission.view_internal_issue == 1">
        <router-link
          v-if="!freemium"
          :to="$url('/all-internal-issues')"
          class="inv-tab"
          :class="isActive(['All Internal Issues','Create Internal Issue','View Internal Issue']) ? 'inv-tab--active' : ''"
        >
          <font-awesome-icon icon="clipboard-list" class="tab-icon" />
          Internal Requisitions
        </router-link>
        <span v-else class="inv-tab inv-tab--locked" @click="freemiumPrompt" title="Upgrade to unlock">
          <font-awesome-icon icon="clipboard-list" class="tab-icon" />
          Internal Requisitions
          <font-awesome-icon icon="lock" class="lock-icon" />
        </span>
      </template>

      <!-- Expired Products -->
      <template v-if="permission.inventory_mgt_module == 1 && permission.view_expired_products == 1">
        <router-link
          v-if="!freemium"
          :to="$url('/expired-products')"
          class="inv-tab"
          active-class="inv-tab--active"
        >
          <font-awesome-icon icon="calendar-times" class="tab-icon" />
          Expired Products
        </router-link>
        <span v-else class="inv-tab inv-tab--locked" @click="freemiumPrompt" title="Upgrade to unlock">
          <font-awesome-icon icon="calendar-times" class="tab-icon" />
          Expired Products
          <font-awesome-icon icon="lock" class="lock-icon" />
        </span>
      </template>

    </div>

    <!-- Upgrade badge -->
    <div v-if="freemium" class="upgrade-badge" @click="$router.push('/subscribe')">
      <font-awesome-icon icon="star" class="me-1" />
      Upgrade to unlock all features
    </div>
  </div>
</template>

<script>
import { computed } from "@vue/runtime-core";
import { useRouter } from "vue-router";
import axios from "axios";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";

export default {
  setup() {
    const store  = useStore();
    const router = useRouter();
    const permission = computed(() => store.state.getCurrentUserPermission);
    const isActive = (names) => names.includes(router.currentRoute.value.name);
    return { permission, isActive };
  },

  data() {
    return {
      freemium: true,
      toast: useToast(),
    };
  },

  mounted() {
    this.activeSubscription();
  },

  methods: {
    activeSubscription() {
      axios.get('/subscription/user').then(response => {
        const active = response.data.data.filter(d => d.status == 1 || d.status == 0);
        this.freemium = active.length === 0;
      }).catch(() => {});
    },

    freemiumPrompt() {
      this.toast.error('Subscribe or upgrade to use this feature');
    },
  },
};
</script>

<style scoped>
.inv-tabs-bar {
  background: #fff;
  border-bottom: 1px solid #e4eaf2;
  padding: 0 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.inv-tabs {
  display: flex;
  align-items: center;
  gap: 2px;
  overflow-x: auto;
  scrollbar-width: none;
}
.inv-tabs::-webkit-scrollbar { display: none; }

/* Base tab */
.inv-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 13px 16px;
  font-size: 13.5px;
  font-weight: 500;
  color: #7a90b0;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
  margin-bottom: -1px;
  cursor: pointer;
}
.inv-tab:hover { color: #004AAD; text-decoration: none; }

/* Active */
.inv-tab--active {
  color: #004AAD !important;
  border-bottom-color: #004AAD !important;
  font-weight: 600;
}

/* Locked / freemium */
.inv-tab--locked {
  color: #aab8cc !important;
  border-bottom-color: transparent !important;
  cursor: pointer;
  position: relative;
}
.inv-tab--locked:hover {
  color: #7a90b0 !important;
}

.tab-icon  { font-size: 13px; opacity: 0.85; }
.lock-icon { font-size: 10px; opacity: 0.6; margin-left: 2px; }

/* Upgrade badge */
.upgrade-badge {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #004AAD, #0066ff);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 20px;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;
  flex-shrink: 0;
}
.upgrade-badge:hover { opacity: 0.88; }

@media (max-width: 576px) {
  .inv-tabs-bar { padding: 0 12px; }
  .inv-tab { padding: 11px 12px; font-size: 13px; }
  .upgrade-badge { font-size: 11px; padding: 4px 10px; }
}
</style>